import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import Model3D from "../Model3D.jsx";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { offerAvailable, claimReward } from "../rewards.js";

/* ==================== RAM DÅSERNE – can knockdown =============================
   A pyramid of ten tin cans on the counter. Five balls: sling them and knock
   EVERY can off the table. Real tumbling physics — cans push each other,
   topple and clatter off the edge. Fewer shots used = better prize.
================================================================================ */

const W = 360, H = 540;
const SHOTS = 5;
const CAN_R = 16;                              // ~25 % op i lineær skala = ~55 % mere areal
const PLAT = { x1: 95, x2: 265, y: 336 };      // table surface (narrow: cans tumble off)
const BALL_R = 12;
const GRAV = 900;
const START = { x: W / 2, y: 468 };            // ball tee
const POWER = 3.4, VMAX = 760;
/* prize by shots used to clear all ten cans */
const prizeForShots = (used) => (used <= 2 ? "pokal" : used === 3 ? "stor" : used === 4 ? "mellem" : "lille");

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    throw() { const c = ensure(); if (!c) return; const o = c.createOscillator(), g = c.createGain(); o.type = "sine"; o.frequency.setValueAtTime(300, c.currentTime); o.frequency.exponentialRampToValueAtTime(700, c.currentTime + 0.18); g.gain.setValueAtTime(0.08, c.currentTime); g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.2); o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.2); },
    clank(v = 1) { const c = ensure(); if (!c) return; tone(180 + Math.random() * 120, c.currentTime, 0.08, "square", 0.1 * v); tone(900 + Math.random() * 400, c.currentTime, 0.05, "square", 0.05 * v); },
    off() { const c = ensure(); if (c) tone(520, c.currentTime, 0.18, "triangle", 0.12); },
    win() { const c = ensure(); if (!c) return; [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14)); },
    lose() { const c = ensure(); if (c) tone(160, c.currentTime, 0.5, "sawtooth", 0.1); },
  };
}

const mkTower = () => {
  const cans = [];
  const rows = [4, 3, 2, 1];
  rows.forEach((count, row) => {
    const y = PLAT.y - CAN_R - row * (CAN_R * 2 - 1);
    const x0 = W / 2 - ((count - 1) * (CAN_R * 2 + 1)) / 2;
    for (let i = 0; i < count; i++)
      cans.push({ x: x0 + i * (CAN_R * 2 + 1), y, vx: 0, vy: 0, ang: Math.random() * 0.06 - 0.03, w: 0, hue: i % 2, asleep: true });
  });
  return cans;
};

export default function Cans({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | finished | failed
  const [hud, setHud] = useState({ shots: 0, cansLeft: 10 });
  const [result, setResult] = useState(null);    // {used}
  const [claimed, setClaimed] = useState(null);
  const [offer, setOffer] = useState(false);     // "so close" rewarded-ad offer
  const [adErr, setAdErr] = useState(false);
  const gameRef = useRef(null);                  // live game state (ad grants a ball into it)

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setClaimed(null);
    setResult(null);
    setOffer(false);
    setAdErr(false);
    setHud({ shots: 0, cansLeft: 10 });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* the "so close" offer: decline → the miss it always was; watch → +1 ball.
     Clearing with the extra ball still counts the real shots used, so the
     prize can never climb past "lille" (pokal requires ≤2 shots — untouchable). */
  const declineOffer = () => {
    setOffer(false);
    const g = gameRef.current;
    audioRef.current.lose();
    setResult({ used: g ? g.shots : SHOTS });
    setPhase("failed");
  };
  const watchAd = () => {
    setAdErr(false);
    claimReward("ekstra_bold", {
      onReward: () => {
        const g = gameRef.current;
        if (g) { g.bonusShots += 1; g.over = false; g.settleT = 0; }
        setOffer(false);
      },
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const audio = audioRef.current;

    const g = {
      cans: mkTower(),
      ball: { x: START.x, y: START.y, vx: 0, vy: 0, live: false, t: 0 },
      aim: null,               // {dx, dy}
      shots: 0, bonusShots: 0, over: false, settleT: 0,
      popups: [],
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__cn = g;

    /* ---- sizing ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input: drag anywhere = slingshot aim, release = throw ---- */
    const canvasPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const cx = (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX) - r.left;
      const cy = (e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY) - r.top;
      return { x: cx * (W / r.width), y: cy * (H / r.height) };
    };
    const down = (e) => {
      e.preventDefault();
      audio.unlock();
      if (g.over || g.ball.live || g.shots >= SHOTS + g.bonusShots) return;
      const p = canvasPos(e);
      g.aim = { sx: p.x, sy: p.y, dx: 0, dy: 0 };
    };
    const move = (e) => {
      if (!g.aim) return;
      const p = canvasPos(e);
      g.aim.dx = p.x - g.aim.sx;
      g.aim.dy = p.y - g.aim.sy;
    };
    const up = () => {
      if (!g.aim) return;
      const { dx, dy } = g.aim;
      g.aim = null;
      if (dy < 14) return;                     // must pull DOWN to sling UP
      let vx = -dx * POWER, vy = -dy * POWER;
      const v = Math.hypot(vx, vy);
      if (v > VMAX) { vx *= VMAX / v; vy *= VMAX / v; }
      Object.assign(g.ball, { x: START.x, y: START.y, vx, vy, live: true, t: 0 });
      g.shots += 1;
      audio.throw();
    };
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: true });
    canvas.addEventListener("touchend", up);

    /* ---- physics ---- */
    const step = (dt) => {
      if (g.over) return;

      // ball flight
      const b = g.ball;
      if (b.live) {
        b.t += dt;
        b.vy += GRAV * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        // ball vs cans (ball is heavier: big push, small recoil)
        for (const c of g.cans) {
          const dx = c.x - b.x, dy = c.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d > 0 && d < CAN_R + BALL_R) {
            const nx = dx / d, ny = dy / d;
            const rel = (b.vx - c.vx) * nx + (b.vy - c.vy) * ny;
            if (rel > 0) {
              c.asleep = false;                 // the hit wakes the tower
              c.vx += nx * rel * 1.75;
              c.vy += ny * rel * 1.75;
              b.vx -= nx * rel * 0.35;
              b.vy -= ny * rel * 0.35;
              c.w += (Math.random() - 0.5) * 8;
              audio.clank(Math.min(1, rel / 400));
            }
            const overlap = CAN_R + BALL_R - d;
            c.x += nx * overlap; c.y += ny * overlap;
          }
        }
        if (b.y > H + 40 || b.x < -40 || b.x > W + 40 || b.t > 4) {
          b.live = false;
          b.x = START.x; b.y = START.y;
        }
      }

      // cans: gravity, pair collisions, table. The tower SLEEPS untouched
      // (hand-rolled circle stacks aren't stable) — impacts wake cans up.
      for (const c of g.cans) {
        if (c.asleep) continue;
        c.vy += GRAV * dt;
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        c.ang += c.w * dt;
      }
      for (let i = 0; i < g.cans.length; i++)
        for (let j = i + 1; j < g.cans.length; j++) {
          const a = g.cans[i], c = g.cans[j];
          if (a.asleep && c.asleep) continue;
          const dx = c.x - a.x, dy = c.y - a.y;
          const d = Math.hypot(dx, dy);
          if (d === 0 || d > CAN_R * 2) continue;
          const nx = dx / d, ny = dy / d;
          const rel = (a.vx - c.vx) * nx + (a.vy - c.vy) * ny;
          if ((a.asleep || c.asleep) && Math.abs(rel) > 40) { a.asleep = false; c.asleep = false; }
          if (a.asleep || c.asleep) continue;   // gentle contact doesn't topple the stack
          const overlap = CAN_R * 2 - d;
          a.x -= nx * overlap / 2; a.y -= ny * overlap / 2;
          c.x += nx * overlap / 2; c.y += ny * overlap / 2;
          if (rel > 0) {
            const k = rel * 0.72;               // tin cans pass the punch along
            a.vx -= nx * k; a.vy -= ny * k;
            c.vx += nx * k; c.vy += ny * k;
            if (rel > 90) audio.clank(Math.min(1, rel / 500));
          }
        }
      // a sleeping can whose support was blasted away must wake and fall
      for (const c of g.cans) {
        if (!c.asleep) continue;
        if (c.y >= PLAT.y - CAN_R - 1) continue;          // rests on the table
        const supported = g.cans.some((o) =>
          o !== c && Math.abs(o.x - c.x) < CAN_R * 1.7 &&
          o.y > c.y + CAN_R * 0.8 && o.y < c.y + CAN_R * 2.4);
        if (!supported) c.asleep = false;
      }
      for (const c of g.cans) {
        if (c.asleep) continue;
        const onPlat = c.x > PLAT.x1 - CAN_R * 0.4 && c.x < PLAT.x2 + CAN_R * 0.4;
        if (onPlat && c.y > PLAT.y - CAN_R && c.y < PLAT.y + CAN_R && c.vy >= 0) {
          c.y = PLAT.y - CAN_R;
          c.vy = c.vy > 130 ? -c.vy * 0.22 : 0;
          c.vx *= 0.965;                         // slick table — they skid right off
          c.w *= 0.9;
          if (Math.abs(c.vx) < 2) c.vx = 0;
        }
      }
      // cans off the table edge/screen are OUT
      const before = g.cans.length;
      g.cans = g.cans.filter((c) => {
        if (c.y < H + 50) return true;
        g.popups.push({ x: Math.max(30, Math.min(W - 30, c.x)), y: H - 60, txt: t("cn_out"), c: "#ffd75e", t: 0 });
        return false;
      });
      if (g.cans.length < before) audio.off();

      // round over?
      if (g.cans.length === 0) {
        g.over = true;
        const used = g.shots;
        audio.win();
        setTimeout(() => {
          setResult({ used });
          if (prizeForShots(used) === "pokal") setClaimed(saveTrophy({ game: "Ram dåserne" }));
          setPhase("finished");
        }, 800);
      } else if (g.shots >= SHOTS + g.bonusShots && !b.live) {
        // let physics settle a moment before calling it a miss
        g.settleT += dt;
        const moving = g.cans.some((c) => Math.abs(c.vx) > 8 || Math.abs(c.vy) > 8);
        if (!moving && g.settleT > 1.2) {
          g.over = true;
          // one chance per game: watch a video → one extra ball
          if (!g.extraOffered && offerAvailable("ekstra_bold")) {
            g.extraOffered = true;
            setOffer(true);
          } else {
            audio.lose();
            setTimeout(() => { setResult({ used: g.shots }); setPhase("failed"); }, 500);
          }
        }
      } else {
        g.settleT = 0;
      }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);

      if (g.shots !== hudRef.current.shots || g.cans.length !== hudRef.current.cansLeft) {
        hudRef.current = { shots: g.shots, cansLeft: g.cans.length };
        setHud(hudRef.current);
      }
    };

    /* ---- drawing ---- */
    const drawCan = (c) => {
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(c.ang);
      ctx.fillStyle = "#d9dde2";
      ctx.beginPath(); ctx.roundRect(-CAN_R + 1, -CAN_R, CAN_R * 2 - 2, CAN_R * 2, 3); ctx.fill();
      ctx.fillStyle = c.hue ? "#C8352E" : "#e0632e";
      ctx.fillRect(-CAN_R + 1, -CAN_R * 0.45, CAN_R * 2 - 2, CAN_R * 0.9);
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "bold 11px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("★", 0, 0);
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.fillRect(-CAN_R + 2, -CAN_R + 1, 4, CAN_R * 2 - 2);
      ctx.strokeStyle = "#8a8f96"; ctx.lineWidth = 1.5;
      ctx.strokeRect(-CAN_R + 1, -CAN_R, CAN_R * 2 - 2, CAN_R * 2);
      ctx.restore();
      ctx.textBaseline = "alphabetic";
    };

    const draw = () => {
      // vintage bod-indre (Gemini-mockup, ROLIG version: ingen hylder/rekvisitter
      // der stjæler fokus — dåserne skal forblive de lyseste elementer)
      const wall = ctx.createLinearGradient(0, 0, 0, H);
      wall.addColorStop(0, "#3a2417");
      wall.addColorStop(0.7, "#2a1810");
      wall.addColorStop(1, "#1c0f09");
      ctx.fillStyle = wall;
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,0,0,0.25)";            // vægplanker
      for (let i = 1; i < 6; i++) {
        ctx.beginPath(); ctx.moveTo(i * 60, 0); ctx.lineTo(i * 60, H - 90); ctx.stroke();
      }
      // trægulv nederst
      const floor = ctx.createLinearGradient(0, H - 90, 0, H);
      floor.addColorStop(0, "#4a2c18");
      floor.addColorStop(1, "#331d0e");
      ctx.fillStyle = floor;
      ctx.fillRect(0, H - 90, W, 90);
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      for (let i = 0; i < 4; i++) {
        ctx.beginPath(); ctx.moveTo(0, H - 70 + i * 18); ctx.lineTo(W, H - 70 + i * 18); ctx.stroke();
      }

      // mørkt rødt gardin bag bordet (dybere end dåsernes røde, så de popper)
      ctx.fillStyle = "#47150f";
      ctx.fillRect(64, 46, W - 128, 300);
      ctx.fillStyle = "rgba(0,0,0,0.22)";              // folder
      for (let i = 0; i < 7; i++) ctx.fillRect(76 + i * 32, 46, 9, 300);
      ctx.fillStyle = "#5a1c14";                        // gardinkappe m. swag
      ctx.beginPath();
      ctx.moveTo(64, 46);
      for (let i = 0; i < 4; i++) ctx.quadraticCurveTo(64 + 29 + i * 58, 84, 64 + 58 + i * 58, 50);
      ctx.lineTo(W - 64, 46);
      ctx.closePath(); ctx.fill();

      // varm glød over scenen
      const grad = ctx.createRadialGradient(W / 2, 250, 40, W / 2, 250, 260);
      grad.addColorStop(0, "rgba(245,201,78,0.14)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 30, W, 340);

      // to lyskæder m. svaj (varme pærer, dæmpet glød)
      for (const [y0, sag, n] of [[64, 26, 9], [96, 20, 8]]) {
        ctx.strokeStyle = "rgba(220,190,140,0.35)";
        ctx.beginPath();
        ctx.moveTo(18, y0);
        ctx.quadraticCurveTo(W / 2, y0 + sag * 2, W - 18, y0);
        ctx.stroke();
        for (let i = 0; i < n; i++) {
          const tpo = (i + 0.5) / n;
          const bx2 = 18 + tpo * (W - 36);
          const by2 = y0 + 2 * sag * tpo * (1 - tpo) * 2;
          const flick = 0.8 + 0.2 * Math.sin(g.t * 3 + i * 1.9 + y0);
          ctx.save();
          ctx.shadowColor = "#ffd98a"; ctx.shadowBlur = 9 * flick;
          ctx.fillStyle = `rgba(255,214,130,${0.9 * flick})`;
          ctx.beginPath(); ctx.arc(bx2, by2 + 4, 3.4, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.fillStyle = "#fff3cf";
          ctx.beginPath(); ctx.arc(bx2, by2 + 3, 1.5, 0, Math.PI * 2); ctx.fill();
        }
      }

      // rope-bunting øverst (rød/creme vimpler på snor)
      ctx.strokeStyle = "rgba(220,190,140,0.5)";
      ctx.beginPath(); ctx.moveTo(0, 22); ctx.quadraticCurveTo(W / 2, 40, W, 22); ctx.stroke();
      for (let i = 0; i < 10; i++) {
        const tpo = (i + 0.5) / 10;
        const fx = tpo * W;
        const fy = 22 + 2 * 18 * tpo * (1 - tpo) * 2;
        ctx.fillStyle = i % 2 ? "#C8352E" : "#F6EED9";
        ctx.beginPath();
        ctx.moveTo(fx - 9, fy); ctx.lineTo(fx + 9, fy); ctx.lineTo(fx, fy + 16);
        ctx.closePath(); ctx.fill();
      }

      // guldmønter på gulvet (stille detalje fra mockuppet)
      ctx.fillStyle = "#c8a557";
      for (const [mx, my2, mr] of [[52, H - 38, 4], [70, H - 30, 3.4], [W - 66, H - 44, 4], [W - 84, H - 26, 3.2], [W - 48, H - 30, 3]]) {
        ctx.beginPath(); ctx.ellipse(mx, my2, mr, mr * 0.45, 0, 0, Math.PI * 2); ctx.fill();
      }

      // solidt træbord m. forkant og ben i perspektiv
      const top = ctx.createLinearGradient(0, PLAT.y, 0, PLAT.y + 20);
      top.addColorStop(0, "#8a5a2e");
      top.addColorStop(1, "#6a3f1e");
      ctx.fillStyle = top;
      ctx.fillRect(PLAT.x1 - 12, PLAT.y, PLAT.x2 - PLAT.x1 + 24, 20);
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      ctx.beginPath(); ctx.moveTo(PLAT.x1 - 6, PLAT.y + 10); ctx.lineTo(PLAT.x2 + 6, PLAT.y + 10); ctx.stroke();
      ctx.fillStyle = "#4f2d14";                        // forkant-skygge
      ctx.fillRect(PLAT.x1 - 12, PLAT.y + 20, PLAT.x2 - PLAT.x1 + 24, 6);
      ctx.fillStyle = "#5a3419";
      ctx.fillRect(PLAT.x1 + 8, PLAT.y + 26, 14, 109);
      ctx.fillRect(PLAT.x2 - 22, PLAT.y + 26, 14, 109);
      ctx.fillStyle = "rgba(0,0,0,0.35)";               // ben-skygge
      ctx.fillRect(PLAT.x1 + 19, PLAT.y + 26, 3, 109);
      ctx.fillRect(PLAT.x2 - 11, PLAT.y + 26, 3, 109);
      ctx.fillStyle = "rgba(255,255,255,0.14)";
      ctx.fillRect(PLAT.x1 - 12, PLAT.y, PLAT.x2 - PLAT.x1 + 24, 4);

      for (const c of g.cans) drawCan(c);

      // aim guide: predicted trajectory dots
      if (g.aim && Math.hypot(g.aim.dx, g.aim.dy) > 8) {
        let vx = -g.aim.dx * POWER, vy = -g.aim.dy * POWER;
        const v = Math.hypot(vx, vy);
        if (v > VMAX) { vx *= VMAX / v; vy *= VMAX / v; }
        let px = START.x, py = START.y;
        ctx.fillStyle = "rgba(246,238,217,0.75)";
        for (let i = 0; i < 14; i++) {
          px += vx * 0.05; vy += GRAV * 0.05; py += vy * 0.05;
          if (py > H) break;
          ctx.beginPath(); ctx.arc(px, py, 3 - i * 0.12, 0, Math.PI * 2); ctx.fill();
        }
        // power meter on the tee
        const pw = Math.min(1, v / VMAX);
        ctx.strokeStyle = pw > 0.85 ? "#e05a4e" : "#f5c94e";
        ctx.lineWidth = 4;
        ctx.beginPath(); ctx.arc(START.x, START.y, BALL_R + 7, -Math.PI / 2, -Math.PI / 2 + pw * Math.PI * 2); ctx.stroke();
        ctx.lineWidth = 1;
      }

      // ball (on the tee or flying) — ren lys bold uden sømme
      const baseball = (x, y, r) => {
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = "#f2ead2"; ctx.fill();
        ctx.strokeStyle = "#b3a880"; ctx.stroke();
        ctx.beginPath(); ctx.arc(x - r * 0.3, y - r * 0.3, r * 0.28, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.fill();
      };
      const b = g.ball;
      const bx = b.live ? b.x : START.x, by = b.live ? b.y : START.y;
      if (g.shots < SHOTS + g.bonusShots || b.live) baseball(bx, by, BALL_R);
      // remaining balls
      for (let i = 0; i < SHOTS + g.bonusShots - g.shots - (b.live ? 0 : 1); i++) {
        baseball(36 + i * 26, H - 30, 9);
      }

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 22px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 4; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 24);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 24);
        ctx.globalAlpha = 1;
      }

      // vignette + messingramme m. nitter (mockup-looket)
      const vg = ctx.createRadialGradient(W / 2, H / 2, 190, W / 2, H / 2, 400);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.3)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
      const brass = ctx.createLinearGradient(0, 0, W, H);
      brass.addColorStop(0, "#c8a557"); brass.addColorStop(0.5, "#8a6d34"); brass.addColorStop(1, "#c8a557");
      ctx.strokeStyle = brass; ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, W - 10, H - 10);
      ctx.strokeStyle = "rgba(255,235,180,0.35)"; ctx.lineWidth = 1.5;
      ctx.strokeRect(10.5, 10.5, W - 21, H - 21);
      ctx.lineWidth = 1;
      ctx.fillStyle = "#6f5720";                       // nitter
      for (const [nx2, ny2] of [[5, 5], [W - 5, 5], [5, H - 5], [W - 5, H - 5], [W / 2, 5], [W / 2, H - 5]]) {
        ctx.beginPath(); ctx.arc(nx2, ny2, 2.4, 0, Math.PI * 2); ctx.fill();
      }
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      // physics substeps for stable stacking
      step(dt / 2); step(dt / 2);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      delete window.__cn;
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
    };
  }, [phase]);
  const hudRef = useRef({ shots: 0, cansLeft: 10 });

  /* ================= UI ================= */
  const prize = result ? prizeForShots(result.used) : null;

  return (
    <div className={"cn-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_cans").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("cn_sign2", { shots: SHOTS })}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("cn_lede", { shots: SHOTS })}</p>
          <ul className="rules">
            <li>{t("cn_rule1")}</li>
            <li>{t("cn_rule2")}</li>
            <li>{t("cn_rule3")}</li>
          </ul>
          <p className="prizenote">{t("cn_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("cn_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("cn_cans", { n: hud.cansLeft })}</span>
            <span className="last">{t("cn_shots", { n: Math.min(hud.shots, SHOTS), max: SHOTS })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && prize !== "pokal" && !claimed && (
        <PrizePicker size={prize} game="Ram dåserne" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("cn_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("cn_trophyline", { n: result ? result.used : 2 })}</p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "finished" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("cn_win")}</div>
            <p className="winline">{t("cn_winline", { n: result ? result.used : SHOTS })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {offer && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("ad_offer_cans_title")}</div>
            <p className="winline">{t("ad_offer_cans", { n: hud.cansLeft })}</p>
            <button className="cta gold" onClick={watchAd}>{t("ad_watch")}</button>
            {adErr && <p className="adfail">{t("ad_fail")}</p>}
            <button className="cta alt" onClick={declineOffer}>{t("ad_no")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("cn_fail")}</div>
            <p className="winline">{t("cn_failline", { n: hud.cansLeft })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.cn-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#16222e;
  background-image:radial-gradient(circle at 50% -10%, #2c4356 0%, #16222e 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:11.5px; font-weight:800; opacity:.75; line-height:1.4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.adfail{font-size:12px; font-weight:800; opacity:.7; margin:8px 0 0;}
.cta.alt{margin-top:10px; background:#2c4356; box-shadow:0 4px 0 #182735; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #182735;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #7a4a26; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(6,12,18,.75); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.gold{background:linear-gradient(180deg,#f5c94e,#c8951d); color:#4a3308;}
.ribbon.dark{background:#182735;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.trophybig{margin:6px 0 2px; display:flex; justify-content:center;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
